// SNIGJ - Supervision Dashboard Page (Hierarchy)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Shield, Eye, Users, Clock, AlertTriangle, Send, CheckCircle, Unlock,
  FileText, Activity, Bell, ChevronRight, RefreshCw, Gavel, X
} from 'lucide-react';
import { toast } from 'sonner';

const SupervisionPage = () => {
  const { user, language } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInjonctionForm, setShowInjonctionForm] = useState(false);
  const [injonctions, setInjonctions] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Injonction form
  const [injForm, setInjForm] = useState({
    type: 'ORDRE',
    title: '',
    description: '',
    priority: 'NORMAL',
    target_roles: [],
    target_province: '',
    deadline: ''
  });

  const targetRoleOptions = [
    { value: 'COMMISSAIRE_PRINCIPAL', label: 'Commissaire Principal' },
    { value: 'COMMISSAIRE_ADJOINT', label: 'Commissaire Adjoint' },
    { value: 'OPJ', label: 'OPJ' },
    { value: 'POLICE_OFFICER', label: 'Officier de Police' },
    { value: 'AGENT_POLICE', label: 'Agent de Police' },
    { value: 'PROCUREUR_REPUBLIQUE', label: 'Procureur de la République' },
    { value: 'SUBSTITUT_PROCUREUR', label: 'Substitut du Procureur' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [supRes, injRes] = await Promise.all([
        api.get('/hierarchy/supervision'),
        api.get('/hierarchy/injonctions')
      ]);
      setData(supRes.data);
      setInjonctions(injRes.data.injonctions || []);
    } catch (err) {
      console.error('Failed to load supervision data:', err);
      if (err.response?.status === 403) {
        toast.error(language === 'fr' ? 'Accès refusé - rôle de supervision requis' : 'Access denied - supervision role required');
      }
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleConfirmDetention = async (detentionId) => {
    try {
      await api.post(`/hierarchy/detention/${detentionId}/confirm`);
      toast.success(language === 'fr' ? 'Garde à vue confirmée' : 'Detention confirmed');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDirectRelease = async (detentionId, personName) => {
    const motif = prompt(language === 'fr' ? `Motif de libération de ${personName}:` : `Release reason for ${personName}:`);
    if (!motif) return;
    try {
      await api.post(`/hierarchy/detention/${detentionId}/release`, { motif });
      toast.success(language === 'fr' ? 'Libération ordonnée' : 'Release ordered');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleSendInjonction = async () => {
    if (!injForm.title || injForm.target_roles.length === 0) {
      toast.error(language === 'fr' ? 'Titre et destinataires requis' : 'Title and targets required');
      return;
    }
    try {
      await api.post('/hierarchy/injonction', injForm);
      toast.success(language === 'fr' ? 'Injonction envoyée' : 'Order sent');
      setShowInjonctionForm(false);
      setInjForm({ type: 'ORDRE', title: '', description: '', priority: 'NORMAL', target_roles: [], target_province: '', deadline: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleAcknowledge = async (injId) => {
    try {
      await api.post(`/hierarchy/injonctions/${injId}/acknowledge`);
      toast.success(language === 'fr' ? 'Injonction acquittée' : 'Acknowledged');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const toggleRole = (role) => {
    setInjForm(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <div className="h-8 w-64 skeleton" />
          <div className="h-96 skeleton" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">{language === 'fr' ? 'Accès refusé ou données non disponibles' : 'Access denied or data unavailable'}</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="supervision-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Eye className="w-6 h-6 text-primary" />
              {language === 'fr' ? 'Supervision Hiérarchique' : 'Hierarchical Supervision'}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {data.supervisor.name} - {data.supervisor.role.replace(/_/g, ' ')}
              {data.supervisor.province && ` | ${data.supervisor.province}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="refresh-supervision">
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              {language === 'fr' ? 'Actualiser' : 'Refresh'}
            </Button>
            {data.supervisor.can_send_injonctions && (
              <Button onClick={() => setShowInjonctionForm(true)} className="gap-2" data-testid="send-injonction-btn">
                <Send className="w-4 h-4" />
                {language === 'fr' ? 'Nouvelle Injonction' : 'New Order'}
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-amber-600" /></div>
              <p className="text-2xl font-bold">{data.stats.active_detentions}</p>
              <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Gardes à vue en cours' : 'Active Detentions'}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-red-600" /></div>
              <p className="text-2xl font-bold">{data.stats.pending_confirmations}</p>
              <p className="text-xs text-muted-foreground">{language === 'fr' ? 'En attente de confirmation' : 'Pending Confirmation'}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-indigo-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-indigo-600" /></div>
              <p className="text-2xl font-bold">{data.stats.total_interpellations}</p>
              <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Interpellations' : 'Apprehensions'}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="detentions" className="space-y-4">
          <TabsList>
            <TabsTrigger value="detentions" className="gap-1">
              <Clock className="w-4 h-4" />
              {language === 'fr' ? 'Gardes à vue' : 'Detentions'}
              {data.stats.pending_confirmations > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-[10px] px-1">{data.stats.pending_confirmations}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="interpellations" className="gap-1">
              <Users className="w-4 h-4" />
              {language === 'fr' ? 'Interpellations' : 'Apprehensions'}
            </TabsTrigger>
            <TabsTrigger value="cases" className="gap-1">
              <FileText className="w-4 h-4" />
              {language === 'fr' ? 'Dossiers' : 'Cases'}
            </TabsTrigger>
            <TabsTrigger value="injonctions" className="gap-1">
              <Gavel className="w-4 h-4" />
              {language === 'fr' ? 'Injonctions' : 'Orders'}
            </TabsTrigger>
          </TabsList>

          {/* Detentions Tab */}
          <TabsContent value="detentions">
            <div className="space-y-4">
              {/* Pending confirmations first */}
              {data.pending_confirmations.length > 0 && (
                <Card className="border-red-200 dark:border-red-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-4 h-4" />
                      {language === 'fr' ? 'En attente de votre confirmation' : 'Awaiting your confirmation'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {data.pending_confirmations.map((det) => (
                      <div key={det.detention_id} className="flex items-center justify-between p-3 rounded border border-red-100 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20" data-testid={`pending-det-${det.detention_id}`}>
                        <div className="min-w-0">
                          <p className="font-medium">{det.person_name}</p>
                          <p className="text-xs text-muted-foreground">{det.motif} | {language === 'fr' ? 'Par' : 'By'}: {det.agent_name} ({det.agent_role})</p>
                          <p className="text-xs text-muted-foreground">{new Date(det.created_at).toLocaleString('fr-FR')}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" onClick={() => handleConfirmDetention(det.detention_id)} className="gap-1 bg-green-600 hover:bg-green-700" data-testid={`confirm-det-${det.detention_id}`}>
                            <CheckCircle className="w-3 h-3" />
                            {language === 'fr' ? 'Confirmer' : 'Confirm'}
                          </Button>
                          {data.supervisor.can_liberate && (
                            <Button size="sm" variant="destructive" onClick={() => handleDirectRelease(det.detention_id, det.person_name)} className="gap-1" data-testid={`release-det-${det.detention_id}`}>
                              <Unlock className="w-3 h-3" />
                              {language === 'fr' ? 'Libérer' : 'Release'}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* All active detentions */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    {language === 'fr' ? 'Toutes les gardes à vue en cours' : 'All active detentions'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.active_detentions.length > 0 ? data.active_detentions.map((det) => (
                    <div key={det.detention_id} className="flex items-center justify-between p-3 rounded border" data-testid={`active-det-${det.detention_id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{det.person_name}</p>
                          {det.confirmed ? (
                            <Badge className="bg-green-100 text-green-700 text-[10px]">{language === 'fr' ? 'Confirmée' : 'Confirmed'}</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px]">{language === 'fr' ? 'Non confirmée' : 'Unconfirmed'}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{det.motif} | {det.duree_heures}h | {det.agent_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {language === 'fr' ? 'Fin prévue' : 'Expected end'}: {det.date_fin_prevue ? new Date(det.date_fin_prevue).toLocaleString('fr-FR') : '-'}
                        </p>
                      </div>
                      {data.supervisor.can_liberate && (
                        <Button size="sm" variant="outline" onClick={() => handleDirectRelease(det.detention_id, det.person_name)} className="gap-1 shrink-0">
                          <Unlock className="w-3 h-3" />
                          {language === 'fr' ? 'Libérer' : 'Release'}
                        </Button>
                      )}
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-4">{language === 'fr' ? 'Aucune garde à vue en cours' : 'No active detentions'}</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Interpellations Tab */}
          <TabsContent value="interpellations">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4 text-indigo-600" />
                  {language === 'fr' ? 'Interpellations récentes' : 'Recent Apprehensions'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.recent_interpellations.length > 0 ? data.recent_interpellations.map((interp, i) => (
                  <div key={interp.interpellation_id || i} className="p-3 rounded border">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{interp.person_name}</p>
                      <Badge variant="outline" className="text-xs">{interp.status || 'ACTIVE'}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{interp.motif}</p>
                    <p className="text-xs text-muted-foreground">{interp.agent_name} - {new Date(interp.created_at).toLocaleString('fr-FR')}</p>
                  </div>
                )) : (
                  <p className="text-sm text-muted-foreground text-center py-4">{language === 'fr' ? 'Aucune interpellation' : 'No apprehensions'}</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cases Tab */}
          <TabsContent value="cases">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  {language === 'fr' ? 'Dossiers récents des subordonnés' : 'Recent Cases from Subordinates'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.recent_cases.length > 0 ? data.recent_cases.map((c, i) => (
                  <div key={c.case_id || i} className="p-3 rounded border flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{c.title || c.case_number}</p>
                      <p className="text-xs text-muted-foreground">{c.case_number} | {c.status}</p>
                    </div>
                    <Badge variant="outline" className="shrink-0">{c.province || '-'}</Badge>
                  </div>
                )) : (
                  <p className="text-sm text-muted-foreground text-center py-4">{language === 'fr' ? 'Aucun dossier' : 'No cases'}</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Injonctions Tab */}
          <TabsContent value="injonctions">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Gavel className="w-4 h-4 text-purple-600" />
                  {language === 'fr' ? 'Injonctions' : 'Orders'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {injonctions.length > 0 ? injonctions.map((inj) => {
                  const isMine = inj.created_by === user?.user_id;
                  const isAcked = inj.acknowledgements?.some(a => a.user_id === user?.user_id);
                  return (
                    <div key={inj.injonction_id} className={`p-3 rounded border ${inj.priority === 'URGENT' ? 'border-red-300 bg-red-50/50 dark:bg-red-950/20' : ''}`} data-testid={`injonction-${inj.injonction_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={`text-[10px] ${inj.priority === 'URGENT' ? 'bg-red-500' : inj.priority === 'NORMAL' ? 'bg-blue-500' : 'bg-gray-500'}`}>{inj.priority}</Badge>
                          <Badge variant="outline" className="text-[10px]">{inj.type}</Badge>
                        </div>
                        {isMine && <Badge className="bg-green-100 text-green-700 text-[10px]">{language === 'fr' ? 'Envoyée' : 'Sent'}</Badge>}
                      </div>
                      <p className="font-medium mt-1">{inj.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{inj.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {language === 'fr' ? 'De' : 'From'}: {inj.created_by_name} ({inj.created_by_role}) - {new Date(inj.created_at).toLocaleString('fr-FR')}
                      </p>
                      {inj.target_roles?.length > 0 && (
                        <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Destinataires' : 'Targets'}: {inj.target_roles.join(', ')}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-muted-foreground">{inj.acknowledgements?.length || 0} {language === 'fr' ? 'acquittement(s)' : 'acknowledgement(s)'}</p>
                        {!isMine && !isAcked && (
                          <Button size="sm" variant="outline" onClick={() => handleAcknowledge(inj.injonction_id)} className="gap-1" data-testid={`ack-inj-${inj.injonction_id}`}>
                            <CheckCircle className="w-3 h-3" />
                            {language === 'fr' ? 'Acquitter' : 'Acknowledge'}
                          </Button>
                        )}
                        {isAcked && <Badge className="bg-green-100 text-green-700 text-[10px]">{language === 'fr' ? 'Acquittée' : 'Acknowledged'}</Badge>}
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-sm text-muted-foreground text-center py-4">{language === 'fr' ? 'Aucune injonction' : 'No orders'}</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Injonction Form Modal */}
      {showInjonctionForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="injonction-modal">
          <Card className="w-full max-w-lg max-h-[85vh] overflow-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                {language === 'fr' ? 'Nouvelle Injonction' : 'New Order'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowInjonctionForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{language === 'fr' ? 'Type' : 'Type'}</Label>
                  <Select value={injForm.type} onValueChange={(v) => setInjForm({ ...injForm, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ORDRE">{language === 'fr' ? 'Ordre' : 'Order'}</SelectItem>
                      <SelectItem value="INSTRUCTION">{language === 'fr' ? 'Instruction' : 'Instruction'}</SelectItem>
                      <SelectItem value="REQUISITION">{language === 'fr' ? 'Réquisition' : 'Requisition'}</SelectItem>
                      <SelectItem value="RAPPEL">{language === 'fr' ? 'Rappel' : 'Reminder'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>{language === 'fr' ? 'Priorité' : 'Priority'}</Label>
                  <Select value={injForm.priority} onValueChange={(v) => setInjForm({ ...injForm, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="URGENT">{language === 'fr' ? 'Urgent' : 'Urgent'}</SelectItem>
                      <SelectItem value="NORMAL">Normal</SelectItem>
                      <SelectItem value="BASSE">{language === 'fr' ? 'Basse' : 'Low'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1">
                <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                <Input value={injForm.title} onChange={(e) => setInjForm({ ...injForm, title: e.target.value })} placeholder={language === 'fr' ? 'Objet de l\'injonction...' : 'Order subject...'} data-testid="inj-title-input" />
              </div>

              <div className="space-y-1">
                <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
                <Textarea value={injForm.description} onChange={(e) => setInjForm({ ...injForm, description: e.target.value })} rows={3} data-testid="inj-desc-input" />
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Destinataires (rôles)' : 'Target roles'}</Label>
                <div className="flex flex-wrap gap-2">
                  {targetRoleOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => toggleRole(opt.value)}
                      className={`px-2 py-1 rounded text-xs border transition-colors ${
                        injForm.target_roles.includes(opt.value) 
                          ? 'bg-primary text-primary-foreground border-primary' 
                          : 'bg-background border-border hover:bg-muted'
                      }`}
                      data-testid={`role-toggle-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <Label>{language === 'fr' ? 'Date limite (optionnel)' : 'Deadline (optional)'}</Label>
                <Input type="datetime-local" value={injForm.deadline} onChange={(e) => setInjForm({ ...injForm, deadline: e.target.value })} />
              </div>

              <Button onClick={handleSendInjonction} className="w-full gap-2" data-testid="submit-injonction-btn">
                <Send className="w-4 h-4" />
                {language === 'fr' ? 'Envoyer l\'injonction' : 'Send Order'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SupervisionPage;
